/**
 * Client-side runbook resolver.
 *
 * Reads pre-resolved values from window.__RUNBOOK_CONFIG__ which is injected
 * by the RunbookConfig server component in layout.tsx.
 *
 * Usage:
 *   import { resolveRunbook, useRunbook } from '@/lib/runbook/client';
 *   const label = resolveRunbook('ui-frontend.shell.header.subtitle', 'Default Text');
 *   // Or as a hook (recommended — handles hydration and runtime switching):
 *   const label = useRunbook('ui-frontend.shell.header.subtitle', 'Default Text');
 */

import { useState, useEffect, useCallback } from 'react';

// resolveRunbook lives in a hook-free module so server code (the route config the server
// template imports) can use it without pulling React client hooks into a server component.
import { resolveRunbook } from './resolve';

export { resolveRunbook };

/**
 * React hook for runbook values that handles SSR/hydration and runtime switching.
 *
 * Renders the default during SSR and initial hydration (no mismatch),
 * then updates to the runbook override after mount via useEffect.
 *
 * Listens for 'runbook-config-changed' events (dispatched by RunbookConfig
 * server component after router.refresh()) so values update instantly when
 * an admin switches runbook mode — no rebuild or page reload needed.
 */
export function useRunbook(namespace: string, defaultValue: string): string {
  const [value, setValue] = useState(defaultValue);

  const resolve = useCallback(() => {
    setValue(resolveRunbook(namespace, defaultValue));
  }, [namespace, defaultValue]);

  // Initial mount — pick up __RUNBOOK_CONFIG__ after hydration
  useEffect(resolve, [resolve]);

  // Re-resolve when runbook mode is switched at runtime
  useEffect(() => {
    window.addEventListener('runbook-config-changed', resolve);
    return () => window.removeEventListener('runbook-config-changed', resolve);
  }, [resolve]);

  return value;
}
